export function shouldProgrammaticallyFocusInlineComposer({
    isMobile,
    isAndroid,
    isEditorSurfaceTarget,
    isContentEditableTarget,
}) {
    // Let the browser place the caret for direct taps in editable text. Calling
    // TipTap's focus('end') here races Android's native caret placement.
    if (isAndroid && isContentEditableTarget) return false
    if (isMobile) return true
    return Boolean(isEditorSurfaceTarget)
}

export function shouldPreventComposerControlFocusTransfer({ isAndroid, isIOS, eventType }) {
    // Phones emit a compatibility mousedown after a touch. Cancelling its focus default keeps
    // the contenteditable focused, so the keyboard stays up while the later click activates
    // the control. Left to it, iOS blurred the editor on every control tap: the keyboard
    // dropped, the phone pill folded with it, and a menu just opened from the pill closed.
    // Cancelling touchstart itself would suppress that synthesized click.
    return Boolean((isAndroid || isIOS) && eventType === 'mousedown')
}

// Whether the press that starts a tap on a folded composer opens it at once
// (the pointer surface — a mouse press expands and focuses in one go), or the
// tap does once it has landed. A phone settles the tap where the finger lifts
// and hit-tests it there: the field rising out of the fold at the press, and
// the toolbar arriving under the finger, sent the tap to the camera button or
// to the thread behind the pill, and the field never focused. On a phone the
// fold waits for the landed tap, or for the focus a direct tap gives the field.
export function shouldExpandCompactComposerOnPointerDown({ isMobile }) {
    return !isMobile
}

// Whether a focus (or a press) on the composer asks the panel to scroll to its
// bottom once the keyboard is up. Android's inline composer sits in the
// thread's flow, so the keyboard's arrival would cover it: the panel scrolls
// its end into view. The bottom-editor pill is not in the flow — it is pinned
// to the panel's foot and rides the resized viewport — so the same scroll only
// throws the reader from wherever they were to the thread's end.
export function shouldRequestScrollToBottomOnKeyboardOpen({
    isAndroid,
    isTitle = false,
    closeModal = false,
    bottomEditor = false,
}) {
    return Boolean(isAndroid && !isTitle && !closeModal && !bottomEditor)
}

// What the keyboard going means for a folded-composer pill: it folds back to
// its line, unless a menu of its own is open (the choice made there decides),
// or a press on one of its controls just before still has its exemption — the
// picker or menu that press opens is what took the keyboard. The exemption is
// a window, not a verdict: once it has closed, a pill still open under no
// keyboard is stuck, its next tap landing wherever the field is not. So the
// fold is retried at the window's end instead of dropped. A pill carrying an
// attachment stays open: the fold keeps only the field's line, so the picture
// just picked would vanish with the keyboard and read as lost.
export function resolveKeyboardDismissFold({
    compact,
    expanded,
    menuOpen = false,
    hasAttachment = false,
    skipRemainingMs = 0,
}) {
    if (!compact || !expanded || hasAttachment) return 'none'
    if (menuOpen) return 'menu'
    if (skipRemainingMs > 0) return 'retry'
    return 'fold'
}

// Media picked into the draft (an image, a video, a file): what a fold of the
// bottom-editor pill would hide with nothing of it left in the field's line.
// Each picked image has its own remove button, numbered by its place in the
// draft (new-answer-image-remove-button-0, -1, …), so images match by prefix.
export const PICKED_MEDIA_SELECTORS = [
    '[data-testid^="new-answer-image-remove-button-"]',
    '[data-testid="new-answer-video-remove-button"]',
    '[data-testid="new-answer-file-remove-button"]',
    'video',
]

// Whether a tap on the pill's field takes focus by hand rather than natively.
// On iOS a native focus has WebKit scroll the focused field into view for the
// keyboard — the whole page panned up where the keyboard overlays it, the
// thread's scroll container thrown to its end under a resizing WebView — and
// there is no reveal to do: the foot moves the pill to the keyboard's edge
// itself. WebKit leaves a programmatic focus alone (ProseMirror focuses with
// preventScroll), so the compat mousedown that would focus natively is
// cancelled and the landed tap focuses the editor at its end. Only while the
// keyboard is down: with it up, a tap places the caret as it should.
export function shouldTakeComposerTapFocus({
    isIOS,
    compact,
    keyboardOpen,
    eventType,
    targetIsContentEditable,
}) {
    return Boolean(
        isIOS && compact && !keyboardOpen && eventType === 'mousedown' && targetIsContentEditable,
    )
}
